import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PublicLayout from '@/components/layout/PublicLayout';

export default function Terms() {
  return (
    <PublicLayout>
      <SEOHead
        title="Conditions Générales d'Utilisation | Clairify"
        description="Conditions générales d'utilisation (CGU) de Clairify. Vos droits, nos engagements, et les conditions d'accès à notre plateforme d'IA business intelligence."
        keywords={['CGU', 'conditions utilisation', 'termes service', 'Clairify']}
        canonicalUrl="https://clairify.fr/terms"
        ogType="website"
      />
      <article className="legal-doc">
      <p className="home-kicker">Légal</p>
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-purple-600 dark:text-purple-400" />
            <h1 className="text-4xl font-bold text-theme-primary">Conditions Générales d'Utilisation et de Vente</h1>
          </div>
          <p className="text-lg text-theme-secondary">
            Applicables à la plateforme Clairify
          </p>
          <p className="text-sm text-theme-tertiary mt-2">Dernière mise à jour : 13 octobre 2025</p>
        </div>

        <div className="space-y-12 text-theme-secondary leading-relaxed">
          {/* Préambule */}
          <section className="bg-gradient-to-br from-purple-500/10 to-indigo-600/5 p-6 rounded-xl border border-purple-600 dark:border-purple-400/20">
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Préambule</h2>
            <p>
              Les présentes Conditions Générales d'Utilisation et de Vente (ci-après "CGU-CGV") régissent l'accès et l'utilisation de la plateforme Clairify, éditée par STELLARMEET, ainsi que l'achat de tokens pour utiliser les services d'analyse de données par intelligence artificielle.
            </p>
            <p className="mt-4">
              <strong className="text-theme-primary">En utilisant Clairify, vous acceptez sans réserve les présentes CGU-CGV.</strong> Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser la plateforme.
            </p>
          </section>

          {/* Article 1 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 1 - Définitions</h2>
            <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border space-y-3">
              <p><strong className="text-theme-primary">Plateforme :</strong> Service en ligne Clairify accessible à l'adresse clairify.ai</p>
              <p><strong className="text-theme-primary">Éditeur :</strong> STELLARMEET, SAS au capital de 1 000€, SIREN 935 065 094</p>
              <p><strong className="text-theme-primary">Utilisateur :</strong> Toute personne physique ou morale accédant à la Plateforme</p>
              <p><strong className="text-theme-primary">Client :</strong> Utilisateur ayant souscrit à un plan payant</p>
              <p><strong className="text-theme-primary">Tokens :</strong> Unités de crédit permettant d'utiliser les services IA de la Plateforme</p>
              <p><strong className="text-theme-primary">Services :</strong> Ensemble des fonctionnalités proposées par Clairify (analyse de données, génération de rapports, visualisations, etc.)</p>
              <p><strong className="text-theme-primary">Données Utilisateur :</strong> Données importées, analysées ou générées par l'Utilisateur sur la Plateforme</p>
            </div>
          </section>

          {/* Article 2 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 2 - Objet</h2>
            <p>
              Les présentes CGU-CGV ont pour objet de définir les conditions et modalités d'accès et d'utilisation de la Plateforme Clairify, ainsi que les droits et obligations des parties dans ce cadre.
            </p>
          </section>

          {/* Article 3 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 3 - Accès et inscription</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">3.1. Conditions d'accès</h3>
            <p>La Plateforme est accessible :</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>À toute personne physique majeure disposant de la pleine capacité juridique</li>
              <li>À toute personne morale représentée par une personne habilitée</li>
              <li>24h/24, 7j/7, sauf cas de force majeure ou maintenance</li>
            </ul>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">3.2. Inscription</h3>
            <p className="mb-3">L'inscription nécessite la fourniture d'informations exactes et à jour :</p>
            <div className="bg-theme-surface/30 p-4 rounded-lg border border-theme-border/50">
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Adresse email valide</li>
                <li>Mot de passe sécurisé (minimum 8 caractères)</li>
                <li>Informations professionnelles (optionnelles mais recommandées)</li>
              </ul>
            </div>
            <p className="mt-4">
              L'Utilisateur s'engage à maintenir ses informations à jour et à ne pas créer plusieurs comptes pour la même personne/entreprise.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">3.3. Identifiants</h3>
            <p>
              L'Utilisateur est seul responsable de la confidentialité de ses identifiants. Toute connexion ou transmission de données effectuée à partir de son compte est réputée avoir été effectuée par lui-même.
            </p>
          </section>

          {/* Article 4 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 4 - Description des services</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">4.1. Services proposés</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: <CheckCircle className="text-green-400" />, title: "Console AI", desc: "Analyse conversationnelle de données par IA" },
                { icon: <CheckCircle className="text-green-400" />, title: "Deep Reports", desc: "Génération de rapports analytiques approfondis" },
                { icon: <CheckCircle className="text-green-400" />, title: "Data Visualization", desc: "Création de graphiques et visualisations interactives" },
                { icon: <CheckCircle className="text-green-400" />, title: "Export PDF", desc: "Téléchargement de rapports au format PDF" },
                { icon: <CheckCircle className="text-green-400" />, title: "Historique", desc: "Conservation des sessions et analyses" },
                { icon: <CheckCircle className="text-green-400" />, title: "Import de données", desc: "Support CSV, JSON, Excel" }
              ].map((service, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-theme-surface/30 p-4 rounded-lg border border-theme-border/50">
                  {service.icon}
                  <div>
                    <p className="font-semibold text-theme-primary">{service.title}</p>
                    <p className="text-sm text-theme-secondary">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">4.2. Disponibilité</h3>
            <p>
              L'Éditeur s'efforce d'assurer une disponibilité optimale de la Plateforme, mais ne garantit pas un accès ininterrompu. Des interruptions peuvent survenir pour maintenance, mises à jour ou cas de force majeure.
            </p>
            <p className="mt-2 text-sm text-theme-secondary">
              SLA (Service Level Agreement) : 99,5% de disponibilité sur 1 mois glissant (hors maintenance programmée).
            </p>
          </section>

          {/* Article 5 - Système de tokens */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 5 - Système de tokens et tarification</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">5.1. Principe</h3>
            <p>
              L'utilisation des services d'analyse IA nécessite l'achat de tokens. Un token représente une unité de calcul consommée lors de l'utilisation de l'IA (traitement de texte, génération de réponses, création de graphiques).
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">5.2. Plans disponibles</h3>
            <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="text-left py-2 text-theme-primary">Plan</th>
                    <th className="text-center py-2 text-theme-primary">Tokens</th>
                    <th className="text-right py-2 text-theme-primary">Prix TTC</th>
                  </tr>
                </thead>
                <tbody className="text-theme-secondary">
                  <tr className="border-b border-theme-border">
                    <td className="py-3">Starter</td>
                    <td className="text-center py-3">100 000</td>
                    <td className="text-right py-3">9,00 €</td>
                  </tr>
                  <tr className="border-b border-theme-border">
                    <td className="py-3">Pro</td>
                    <td className="text-center py-3">500 000</td>
                    <td className="text-right py-3">19,00 €</td>
                  </tr>
                  <tr className="border-b border-theme-border">
                    <td className="py-3">Business</td>
                    <td className="text-center py-3">2 000 000</td>
                    <td className="text-right py-3">69,00 €</td>
                  </tr>
                  <tr>
                    <td className="py-3">Enterprise</td>
                    <td className="text-center py-3">10 000 000</td>
                    <td className="text-right py-3">199,00 €</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-theme-tertiary mt-4">
                Prix indicatifs, susceptibles de modification. TVA 20% applicable (France). Tokens valables à vie, sans date d'expiration.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">5.3. Achat de tokens</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Les tokens sont achetés sous forme de packs prépayés (paiement one-time, pas d'abonnement)</li>
              <li>Le paiement s'effectue par carte bancaire via Stripe (paiement sécurisé PCI-DSS)</li>
              <li>Les tokens sont crédités instantanément après validation du paiement</li>
              <li>Les tokens ne sont ni remboursables, ni échangeables, ni transférables</li>
              <li>Les tokens n'ont pas de date d'expiration (valables à vie)</li>
            </ul>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">5.4. Consommation des tokens</h3>
            <p className="mb-3">La consommation de tokens dépend de la complexité des requêtes :</p>
            <div className="bg-theme-surface/30 p-4 rounded-lg border border-theme-border/50">
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Question simple (Console AI) : ~500-2 000 tokens</li>
                <li>Analyse de données complexe : ~2 000-5 000 tokens</li>
                <li>Deep Report complet : ~15 000-40 000 tokens</li>
                <li>Génération de graphiques : ~1 000-3 000 tokens</li>
              </ul>
            </div>
            <p className="mt-4 text-sm text-theme-secondary">
              Le nombre exact de tokens consommés est affiché après chaque requête et consultable dans l'historique des transactions.
            </p>
          </section>

          {/* Article 6 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 6 - Modalités de paiement</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">6.1. Moyens de paiement</h3>
            <p>Les paiements sont acceptés exclusivement par carte bancaire (Visa, Mastercard, American Express) via Stripe.</p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">6.2. Sécurité</h3>
            <p>
              Les transactions sont sécurisées par Stripe (certifié PCI-DSS niveau 1). Vos données bancaires ne transitent jamais par nos serveurs et ne sont pas stockées par STELLARMEET.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">6.3. Facturation</h3>
            <p>
              Une facture est automatiquement générée et envoyée par email après chaque achat. Les factures sont également accessibles dans votre espace client.
            </p>
          </section>

          {/* Article 7 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 7 - Droit de rétractation</h2>
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-6 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="font-semibold text-theme-primary mb-2">Information importante</p>
                  <p>
                    Conformément à l'article L221-28 du Code de la consommation, <strong className="text-theme-primary">le droit de rétractation ne s'applique pas</strong> aux contenus numériques fournis immédiatement après la commande.
                  </p>
                  <p className="mt-3">
                    En achetant des tokens, vous acceptez expressément que les tokens soient crédités immédiatement et renoncez à votre droit de rétractation de 14 jours.
                  </p>
                  <p className="mt-3 text-sm text-theme-secondary">
                    Exception : En cas de défaut technique empêchant l'utilisation des tokens, un remboursement pourra être demandé dans les 30 jours.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Article 8 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 8 - Obligations de l'Utilisateur</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">8.1. Usage conforme</h3>
            <p className="mb-3">L'Utilisateur s'engage à utiliser la Plateforme conformément à sa destination et à ne pas :</p>
            <div className="space-y-2">
              {[
                "Violer les lois et règlements en vigueur",
                "Porter atteinte aux droits de tiers (propriété intellectuelle, vie privée, etc.)",
                "Diffuser des contenus illicites, diffamatoires, racistes, violents ou pornographiques",
                "Tenter de contourner les mesures de sécurité ou d'accéder à des zones non autorisées",
                "Utiliser des robots, scripts ou outils automatisés sans autorisation",
                "Surcharger ou perturber le fonctionnement de la Plateforme",
                "Revendre, sous-licencier ou transférer son accès à des tiers",
                "Extraire ou copier systématiquement des données de la Plateforme"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-theme-surface/30 p-3 rounded-lg border border-theme-border/50">
                  <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm">{item}</p>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">8.2. Données importées</h3>
            <p>
              L'Utilisateur garantit disposer de tous les droits nécessaires sur les données qu'il importe sur la Plateforme et s'engage à respecter le RGPD si ces données contiennent des informations personnelles.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">8.3. Exactitude des informations</h3>
            <p>
              L'Utilisateur s'engage à fournir des informations exactes lors de son inscription et à les maintenir à jour.
            </p>
          </section>

          {/* Article 9 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 9 - Propriété intellectuelle</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">9.1. Propriété de la Plateforme</h3>
            <p>
              La Plateforme, son code source, son design, ses logos, ses bases de données et tous les éléments qui la composent sont la propriété exclusive de STELLARMEET ou de ses partenaires. Toute reproduction, représentation ou exploitation non autorisée constitue une contrefaçon.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">9.2. Licence d'utilisation</h3>
            <p>
              STELLARMEET concède à l'Utilisateur une licence personnelle, non exclusive, non transférable et révocable d'utilisation de la Plateforme, strictement limitée à l'usage des Services.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">9.3. Données Utilisateur</h3>
            <p className="mb-3">
              Les données importées et les contenus créés par l'Utilisateur (rapports, analyses) restent sa propriété exclusive. STELLARMEET s'engage à :
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-theme-secondary">
              <li>Ne pas les divulguer à des tiers sans autorisation</li>
              <li>Ne pas les utiliser à des fins commerciales</li>
              <li>Les supprimer à la demande de l'Utilisateur (sauf obligations légales)</li>
            </ul>
            <p className="mt-4 text-sm text-theme-secondary">
              Exception : STELLARMEET peut utiliser des données anonymisées et agrégées à des fins statistiques et d'amélioration du service.
            </p>
          </section>

          {/* Article 10 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 10 - Responsabilité et garanties</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">10.1. Limitation de responsabilité</h3>
            <p className="mb-3">STELLARMEET ne saurait être tenue responsable :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Des interruptions de service dues à des cas de force majeure ou à des tiers</li>
              <li>De la perte de données résultant d'une action de l'Utilisateur</li>
              <li>Des décisions prises par l'Utilisateur sur la base des analyses fournies</li>
              <li>Des dommages indirects (perte de chiffre d'affaires, perte de clientèle, etc.)</li>
              <li>De l'utilisation non conforme de la Plateforme</li>
            </ul>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">10.2. Nature des analyses</h3>
            <div className="bg-purple-600 dark:bg-purple-500/10 border border-purple-600 dark:border-purple-400/20 p-6 rounded-xl">
              <p className="font-semibold text-theme-primary mb-2">Avertissement important</p>
              <p>
                Les analyses et rapports générés par Clairify sont fournis <strong className="text-theme-primary">à titre informatif uniquement</strong>. Ils ne constituent en aucun cas des conseils juridiques, financiers, comptables ou stratégiques professionnels.
              </p>
              <p className="mt-3">
                L'Utilisateur reste seul responsable de ses décisions et doit faire appel à des professionnels qualifiés pour toute décision importante.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">10.3. Garantie de service</h3>
            <p>
              STELLARMEET s'engage à fournir un service conforme à l'état de l'art, mais ne garantit pas l'absence totale d'erreurs ou d'interruptions. En cas de dysfonctionnement majeur, STELLARMEET s'engage à rétablir le service dans les meilleurs délais.
            </p>
          </section>

          {/* Article 11 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 11 - Suspension et résiliation</h2>
            
            <h3 className="text-xl font-semibold text-theme-primary mb-3">11.1. Suspension par l'Éditeur</h3>
            <p className="mb-3">
              STELLARMEET se réserve le droit de suspendre ou résilier immédiatement l'accès d'un Utilisateur en cas de :
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Non-respect des présentes CGU-CGV</li>
              <li>Utilisation frauduleuse ou abusive</li>
              <li>Activité suspecte ou illégale</li>
              <li>Réquisition judiciaire</li>
            </ul>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">11.2. Résiliation par l'Utilisateur</h3>
            <p>
              L'Utilisateur peut supprimer son compte à tout moment via les paramètres de la Plateforme ou en contactant le support. Les tokens restants ne sont pas remboursables.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">11.3. Effets de la résiliation</h3>
            <p>
              En cas de résiliation, l'Utilisateur perd immédiatement l'accès à la Plateforme et à ses données (sauf export préalable). Les tokens non utilisés sont perdus sans compensation.
            </p>
          </section>

          {/* Article 12 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 12 - Protection des données personnelles</h2>
            <p>
              Le traitement des données personnelles est effectué conformément au RGPD. Pour plus d'informations, consultez notre <Link to="/privacy" className="text-purple-600 dark:text-purple-400 hover:underline font-semibold">Politique de Confidentialité</Link>.
            </p>
          </section>

          {/* Article 13 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 13 - Modifications des CGU-CGV</h2>
            <p>
              STELLARMEET se réserve le droit de modifier les présentes CGU-CGV à tout moment. Les Utilisateurs seront informés des modifications substantielles par email et/ou notification sur la Plateforme.
            </p>
            <p className="mt-4">
              La poursuite de l'utilisation de la Plateforme après modification vaut acceptation des nouvelles CGU-CGV.
            </p>
          </section>

          {/* Article 14 */}
          <section>
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Article 14 - Droit applicable et juridiction</h2>
            <p>
              Les présentes CGU-CGV sont régies par le droit français. En cas de litige, une solution amiable sera recherchée avant toute action judiciaire.
            </p>
            <p className="mt-4">
              À défaut d'accord amiable, les tribunaux compétents de Paris seront seuls compétents, sauf disposition légale contraire.
            </p>

            <h3 className="text-xl font-semibold text-theme-primary mb-3 mt-6">Médiation de la consommation</h3>
            <p className="mb-3">
              Conformément à l'article L612-1 du Code de la consommation, le Client a le droit de recourir gratuitement à un médiateur de la consommation en cas de litige :
            </p>
            <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
              <p><strong className="text-theme-primary">Médiateur :</strong> Médiation de la Consommation (CM2C)</p>
              <p>14 rue Saint Jean, 75017 Paris</p>
              <p className="mt-2">Site web : <a href="https://www.cm2c.net" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://www.cm2c.net</a></p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-br from-purple-500/10 to-indigo-600/5 p-8 rounded-2xl border border-purple-600 dark:border-purple-400/20">
            <h2 className="text-2xl font-semibold text-theme-primary mb-4">Contact</h2>
            <p className="mb-6">Pour toute question relative aux présentes CGU-CGV :</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-theme-primary mb-2">STELLARMEET</p>
                <p className="text-sm text-theme-secondary">25 rue de Ponthieu</p>
                <p className="text-sm text-theme-secondary">75008 Paris, France</p>
              </div>
              <div>
                <p className="font-semibold text-theme-primary mb-2">Email</p>
                <p className="text-sm">
                  <a href="mailto:contact@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">contact@stellarmeet.com</a>
                </p>
                <p className="text-sm mt-2">
                  <a href="mailto:support@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">support@stellarmeet.com</a>
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-theme-border flex flex-wrap gap-4 text-sm">
          <Link to="/legal" className="text-purple-600 dark:text-purple-400 hover:underline">Mentions Légales</Link>
          <Link to="/privacy" className="text-purple-600 dark:text-purple-400 hover:underline">Politique de Confidentialité</Link>
          <Link to="/cookies" className="text-purple-600 dark:text-purple-400 hover:underline">Politique de Cookies</Link>
        </div>
      </article>
 </PublicLayout>
 );
}
